import Header from '@/components/layouts/Header';
import {
  PasswordChangeModal,
  UserAddModal,
  UserDeleteConfirmModal,
  UserEditModal,
} from '@/components/modals';
import { omit } from '@/functions/omit';
import type { NewUser } from '@/types/site';
import { useUserData } from '../hooks/useUserData';
import { useUserModals } from '../hooks/useUserModals';
import UserPaginationPanel from './UserPaginationPanel';
import UserSearchBar from './UserSearchBar';
import UserTable from './UserTable';

export default function UserManagement() {
  const userData = useUserData();
  const modalState = useUserModals();

  const handleDeleteConfirm = async () => {
    if (!modalState.selectedUserForDelete) return;

    try {
      await userData.actions.deleteUser(
        modalState.selectedUserForDelete.toString()
      );
      modalState.actions.closeDeleteConfirm();
    } catch (error) {
      console.error('유저 삭제 오류:', error);
      alert('유저 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleEditSave = async () => {
    if (!modalState.selectedUserForEdit) return;

    try {
      const { id, ...rest } = modalState.selectedUserForEdit;
      const payload = omit(rest, [
        'createdTimestamp',
        'editedTimestamp',
        'cityAddress',
      ]);
      await userData.actions.updateUser(id, payload);
      modalState.actions.closeEditModal();
    } catch (error) {
      console.error('유저 수정 오류:', error);
      alert('유저 정보 수정 중 오류가 발생했습니다.');
    }
  };

  const handleAddUser = async (newUser: NewUser) => {
    try {
      await userData.actions.addUser(newUser);
      modalState.actions.closeAddModal();
    } catch (error) {
      console.error('유저 추가 오류:', error);
      throw error; // UserAddModal에서 에러를 처리하도록 다시 던짐
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">회원 관리</h1>
          <p className="text-gray-600">
            시스템의 회원 정보 및 권한을 관리합니다
          </p>
        </div>

        <UserSearchBar
          searchTerm={userData.searchTerm}
          onSearchChange={userData.actions.setSearchTerm}
          onAddUser={modalState.actions.openAddModal}
          totalUsers={userData.users.length}
          filteredUsers={userData.filteredUsers.length}
        />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              회원 목록{` `}
              {userData.searchTerm && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({userData.filteredUsers.length}명 결과)
                </span>
              )}
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">페이지당 항목:</span>
                <select
                  value={userData.itemsPerPage}
                  onChange={(e) => {
                    userData.actions.setItemsPerPage(Number(e.target.value));
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-8"
                >
                  <option value={5}>5명</option>
                  <option value={10}>10명</option>
                  <option value={15}>15명</option>
                  <option value={20}>20명</option>
                  <option value={25}>25명</option>
                  <option value={50}>50명</option>
                  <option value={userData.filteredUsers.length}>
                    전체 ({userData.filteredUsers.length}명)
                  </option>
                </select>
              </div>
              <div className="text-sm text-gray-500">
                {userData.totalPages > 1
                  ? `총 ${userData.totalPages}페이지`
                  : '1페이지'}
              </div>
            </div>
          </div>

          <UserTable
            users={userData.currentUsers}
            sortField={userData.sortField}
            sortDirection={userData.sortDirection}
            onSort={userData.actions.handleSort}
            onEdit={modalState.actions.openEditModal}
            onDelete={modalState.actions.openDeleteConfirm}
          />

          <UserPaginationPanel
            currentPage={userData.currentPage}
            totalPages={userData.totalPages}
            startIndex={userData.startIndex}
            endIndex={userData.endIndex}
            totalItems={userData.filteredUsers.length}
            onPageChange={userData.actions.setCurrentPage}
          />
        </div>
      </main>

      <UserDeleteConfirmModal
        isOpen={modalState.deleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={modalState.actions.closeDeleteConfirm}
      />

      <UserEditModal
        isOpen={modalState.editModalOpen}
        user={modalState.selectedUserForEdit}
        onSave={handleEditSave}
        onCancel={modalState.actions.closeEditModal}
        onUserChange={modalState.actions.updateSelectedUserForEdit}
        onPasswordChangeClick={modalState.actions.openPasswordChange}
      />

      <UserAddModal
        isOpen={modalState.addModalOpen}
        onAdd={handleAddUser}
        onCancel={modalState.actions.closeAddModal}
      />

      <PasswordChangeModal
        isOpen={modalState.passwordChangeOpen}
        onClose={modalState.actions.closePasswordChange}
        skipCurrentPassword
        targetUserId={modalState.selectedUserForEdit?.id}
      />
    </div>
  );
}
